// src/pages/Licenses.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    licensesAPI,
    ClientLicense,
    ClientDevice,
    LicenseCollectRequest,
} from '@/services/api';
import {
    Loader2,
    Award,
    Search as SearchIcon,
    Clipboard,
    Filter as FilterIcon,
    ChevronRight,
    ChevronLeft,
    Cpu,
    ChevronDown,
    ChevronUp,
    Calendar,
    Clock,
    CheckCircle2,
    XCircle,
    Sparkles,
    RefreshCw,
    SortAsc,
    SortDesc,
    ListCollapse,
    ListStart,
} from 'lucide-react';
import CollectRequestModal from '@/components/CollectRequestModal';

/* ---------- Helpers ---------- */
const fa = new Intl.NumberFormat('fa-IR');
const faDate = (d?: string | number | Date | null) =>
    d ? new Date(d).toLocaleDateString('fa-IR') : '—';

const calculateDaysRemaining = (expiresAt?: string | null): number | null => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/** ✅ فقط ۳ روز اول بعد از صدور، دکمه درخواست لغو رو نشون بده */
const canRequestCollection = (issuedAt?: string | null) => {
    if (!issuedAt) return false;
    const issued = new Date(issuedAt);
    const now = new Date();
    const diffDays = (now.getTime() - issued.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays < 3; // بین روز ۰ تا ۳
};

const humanActive = (s?: string | null) => {
    const ss = String(s || '').toLowerCase();
    if (ss === 'active') return 'فعال';
    if (ss === 'expired') return 'منقضی';
    return s || '—';
};

const activeVariant = (s?: string | null) =>
    String(s || '').toLowerCase() === 'active' ? 'default' : 'destructive';

const markMatch = (text: string, q: string) => {
    if (!q) return text;
    try {
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return (
            <>
                {parts.map((p, i) =>
                    p.toLowerCase() === q.toLowerCase() ? (
                        <mark key={i} className="bg-yellow-200 px-0.5 rounded">
                            {p}
                        </mark>
                    ) : (
                        <span key={i}>{p}</span>
                    ),
                )}
            </>
        );
    } catch {
        return text;
    }
};

const DaysCounter = ({ expiresAt, status }: { expiresAt?: string | null; status: string }) => {
    const days = calculateDaysRemaining(expiresAt);
    if (status.toLowerCase() !== 'active' || days === null) {
        return <span className="text-gray-400 text-xs">—</span>;
    }
    let colorClass = 'text-emerald-700 bg-emerald-50 border-emerald-200';
    let icon = <CheckCircle2 className="h-3.5 w-3.5" />;
    if (days < 0) {
        colorClass = 'text-red-600 bg-red-50 border-red-200';
        icon = <XCircle className="h-3.5 w-3.5" />;
    } else if (days <= 30) {
        colorClass = 'text-orange-600 bg-orange-50 border-orange-200';
        icon = <Clock className="h-3.5 w-3.5" />;
    }
    return (
        <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-medium text-xs ${colorClass}`}
        >
            {icon}
            <span>{days < 0 ? 'منقضی شده' : `${fa.format(Math.abs(days))} روز`}</span>
        </div>
    );
};

/* ---------- Component ---------- */
const Licenses: React.FC = () => {
    const [licenses, setLicenses] = useState<ClientLicense[]>([]);
    const [activeCount, setActiveCount] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedLicenses, setExpandedLicenses] = useState<Set<string>>(new Set());
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<ClientLicense | null>(null);

    // فیلترها
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [q, setQ] = useState('');
    const [statusActive, setStatusActive] =
        useState<'all' | 'active' | 'inactive'>('all');

    // مرتب‌سازی
    type SortKey = 'created_at' | 'expires_at' | 'status' | 'product' | 'devices' | 'license_key';
    const [sortKey, setSortKey] = useState<SortKey>('created_at');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    // صفحه‌بندی
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // ✅ درخواست‌های جمع‌آوری از بک‌اند
    const [cancelRequests, setCancelRequests] = useState<LicenseCollectRequest[]>([]);
    const [collectLoadingId, setCollectLoadingId] = useState<number | null>(null);

    /* ---------- Load data from API ---------- */
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setError('');

                // ۱) لایسنس‌ها
                const res = await licensesAPI.getLicenses(); // { licenses, active_count }
                setLicenses(res.licenses || []);
                setActiveCount(res.active_count || 0);

                // ۲) لیست درخواست‌های جمع‌آوری
                try {
                    const collectRes = await licensesAPI.getCollectRequests();
                    setCancelRequests(collectRes || []);
                } catch (e) {
                    console.warn('خطا در دریافت درخواست‌های جمع‌آوری', e);
                }
            } catch (e: any) {
                setError(e?.message || 'خطا در دریافت لایسنس‌ها');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const toggleExpanded = (licenseKey: string) => {
        setExpandedLicenses((prev) => {
            const n = new Set(prev);
            if (n.has(licenseKey)) n.delete(licenseKey);
            else n.add(licenseKey);
            return n;
        });
    };

    const expandAll = () =>
        setExpandedLicenses(new Set(licenses.map((l) => l.license_key)));
    const collapseAll = () => setExpandedLicenses(new Set());

    // آیا برای این لایسنس قبلاً درخواست جمع‌آوری ثبت شده؟
    const hasCancelRequest = (licenseKey: string) =>
        cancelRequests.some((r) => r.license_key === licenseKey);

    const submitCollectRequest = async (note: string) => {
        if (!selectedLicense) return;

        try {
            setCollectLoadingId(selectedLicense.id);

            const created = await licensesAPI.requestCollect(
                selectedLicense.id,
                note
            );

            // اضافه به لیست درخواست‌ها
            setCancelRequests((prev) => [...prev, created]);

            setModalOpen(false);
            setSelectedLicense(null);
        } catch (e: any) {
            alert(e?.message || 'خطا در ثبت درخواست');
        } finally {
            setCollectLoadingId(null);
        }
    };

    // خلاصه
    const totals = useMemo(() => {
        const count = licenses.length;
        const assigned = licenses.filter(
            (l) => (l.devices?.length ?? 0) > 0
        ).length;
        const free = count - assigned;
        return { count, free, assigned };
    }, [licenses]);

    // فیلتر + مرتب‌سازی
    const filteredSorted = useMemo(() => {
        const text = q.trim().toLowerCase();
        const filtered = licenses.filter((l) => {
            const textMatch =
                !text ||
                [l.license_key, l.product_title, String(l.product_id), l.status]
                    .filter(Boolean)
                    .some((t) => String(t).toLowerCase().includes(text));

            const s = String(l.status || '').toLowerCase();
            const activeMatch =
                statusActive === 'all' ||
                (statusActive === 'active' && s === 'active') ||
                (statusActive === 'inactive' && s !== 'active');

            return textMatch && activeMatch;
        });

        const sorted = [...filtered].sort((a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            switch (sortKey) {
                case 'created_at':
                    return (
                        (new Date(a.created_at || a.issued_at || 0).getTime() -
                            new Date(b.created_at || b.issued_at || 0).getTime()) * dir
                    );
                case 'expires_at':
                    return (
                        (new Date(a.expires_at || 0).getTime() -
                            new Date(b.expires_at || 0).getTime()) * dir
                    );
                case 'status':
                    return a.status.localeCompare(b.status) * dir;
                case 'product':
                    return (a.product_title || '').localeCompare(b.product_title || '') * dir;
                case 'devices':
                    return (
                        ((a.devices?.length || 0) - (b.devices?.length || 0)) * dir
                    );
                case 'license_key':
                    return a.license_key.localeCompare(b.license_key) * dir;
                default:
                    return 0;
            }
        });

        return sorted;
    }, [licenses, q, statusActive, sortKey, sortDir]);

    // صفحه‌بندی
    const totalItems = filteredSorted.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const pageSlice = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredSorted.slice(start, start + pageSize);
    }, [filteredSorted, page, pageSize]);

    /* ---------- Render ---------- */
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
                    <p className="text-emerald-700 font-medium">در حال بارگذاری...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6" dir="rtl">
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50"
            dir="rtl"
        >
            {/* سربرگ */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 p-5 md:p-8 shadow-2xl">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-15"></div>
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
                    <div className="text-white space-y-2 max-w-xl">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-2xl md:text-4xl font-bold">مدیریت لایسنس‌ها</h1>
                        </div>
                        <p className="text-emerald-100 text-sm md:text-lg">
                            کنترل کامل لایسنس‌ها و دستگاه‌های متصل، با نمایش به سبک کارت پرواز
                        </p>
                    </div>
                    <div className="w-full md:w-auto">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white border border-white/30">
                                <div className="text-[11px] md:text-xs opacity-90">کل لایسنس‌ها</div>
                                <div className="text-xl md:text-2xl font-bold">
                                    {fa.format(totals.count)}
                                </div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white border border-white/30">
                                <div className="text-[11px] md:text-xs opacity-90">فعال</div>
                                <div className="text-xl md:text-2xl font-bold text-emerald-200">
                                    {fa.format(activeCount)}
                                </div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white border border-white/30">
                                <div className="text-[11px] md:text-xs opacity-90">
                                    دارای دستگاه متصل
                                </div>
                                <div className="text-xl md:text-2xl font-bold text-sky-200">
                                    {fa.format(totals.assigned)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* فیلترها و ابزار */}
            <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                <CardHeader className="py-4 bg-gradient-to-r from-emerald-50 to-emerald-50/70">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
                                <FilterIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-emerald-900 text-lg">
                                    فیلترها و جستجو
                                </CardTitle>
                                <CardDescription className="text-emerald-700">
                                    تنظیمات نمایش لایسنس‌ها و دستگاه‌ها
                                </CardDescription>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => setFiltersOpen((v) => !v)}
                            >
                                {filtersOpen ? (
                                    <ChevronUp className="ml-2 h-4 w-4" />
                                ) : (
                                    <ChevronDown className="ml-2 h-4 w-4" />
                                )}
                                {filtersOpen ? 'بستن فیلترها' : 'نمایش فیلترها'}
                            </Button>
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={expandAll}
                            >
                                <ListStart className="ml-2 h-4 w-4" />
                                باز کردن همه
                            </Button>
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={collapseAll}
                            >
                                <ListCollapse className="ml-2 h-4 w-4" />
                                بستن همه
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                {filtersOpen && (
                    <CardContent className="space-y-4 pt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="space-y-2 lg:col-span-2">
                                <Label className="text-emerald-900 font-medium">جستجو</Label>
                                <div className="relative">
                                    <SearchIcon className="absolute left-3 top-3.5 h-5 w-5 text-emerald-300" />
                                    <Input
                                        value={q}
                                        onChange={(e) => {
                                            setQ(e.target.value);
                                            setPage(1);
                                        }}
                                        placeholder="جستجو در کلید لایسنس، محصول، وضعیت..."
                                        className="pl-11 h-11 border-emerald-200 focus:ring-emerald-500 focus:border-emerald-500 rounded-xl"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-emerald-900 font-medium">وضعیت</Label>
                                <select
                                    value={statusActive}
                                    onChange={(e) => {
                                        setStatusActive(e.target.value as any);
                                        setPage(1);
                                    }}
                                    className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                    <option value="all">🔘 همه</option>
                                    <option value="active">✅ فعال</option>
                                    <option value="inactive">❌ غیرفعال/منقضی</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="space-y-2 lg:col-span-1">
                                <Label className="text-emerald-900 font-medium">
                                    مرتب‌سازی
                                </Label>
                                <div className="flex gap-2">
                                    <select
                                        value={sortKey}
                                        onChange={(e) => {
                                            setSortKey(e.target.value as any);
                                            setPage(1);
                                        }}
                                        className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    >
                                        <option value="created_at">تاریخ ایجاد/صدور</option>
                                        <option value="expires_at">تاریخ انقضا</option>
                                        <option value="status">وضعیت</option>
                                        <option value="product">نام محصول</option>
                                        <option value="devices">تعداد دستگاه</option>
                                        <option value="license_key">کلید لایسنس</option>
                                    </select>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                        onClick={() =>
                                            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
                                        }
                                    >
                                        {sortDir === 'asc' ? (
                                            <SortAsc className="ml-2 h-4 w-4" />
                                        ) : (
                                            <SortDesc className="ml-2 h-4 w-4" />
                                        )}
                                        {sortDir === 'asc' ? 'صعودی' : 'نزولی'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => {
                                    setQ('');
                                    setStatusActive('all');
                                    setSortKey('created_at');
                                    setSortDir('desc');
                                    setPage(1);
                                }}
                            >
                                ریست فیلترها
                            </Button>
                        </div>
                    </CardContent>
                )}
            </Card>

            {/* لیست لایسنس‌ها */}
            {licenses.length === 0 ? (
                <Card className="shadow-lg border-emerald-100 bg-white/80 backdrop-blur-sm">
                    <CardContent className="text-center py-16">
                        <Award className="h-16 w-16 mx-auto mb-4 text-emerald-200" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            هیچ لایسنسی یافت نشد
                        </h3>
                        <p className="text-gray-500">
                            لایسنس‌های جدید اینجا نمایش داده می‌شوند
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="space-y-4">
                        {pageSlice.map((license) => {
                            const isExpanded = expandedLicenses.has(license.license_key);
                            const deviceCount = license.devices?.length || 0;
                            const productTitle =
                                license.product_title || `محصول ${license.product_id}`;
                            const qn = q.trim();

                            const showCancelButton =
                                license.status.toLowerCase() === 'active' &&
                                canRequestCollection(license.issued_at) &&
                                !hasCancelRequest(license.license_key);

                            return (
                                <div
                                    key={license.license_key}
                                    className="relative overflow-hidden rounded-2xl border border-emerald-600/70 bg-gradient-to-br from-emerald-50/70 to-white shadow-md hover:shadow-xl transition-all duration-300"
                                >
                                    {/* نوار بالایی به سبک کارت پرواز */}
                                    <div className="flex flex-col md:flex-row md:items-stretch">
                                        {/* ستون اصلی اطلاعات */}
                                        <div className="flex-1 p-4 md:p-5 flex flex-col justify-between border-b md:border-b-0 md:border-l border-dashed border-emerald-100">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-right">
                                                {/* محصول و کلید */}
                                                <div className="space-y-2">
                                                    <div className="text-[11px] text-gray-500 font-semibold">
                                                        محصول
                                                    </div>
                                                    <div className="text-lg font-bold text-gray-900">
                                                        {markMatch(productTitle, qn)}
                                                    </div>

                                                    {/* فقط چیپ کلید لایسنس، بدون شناسه محصول */}
                                                    <div
                                                        className="mt-2 text-[11px] font-mono text-emerald-700 bg-emerald-50 inline-flex px-2 py-1 rounded-md border border-emerald-100"
                                                        dir="ltr"
                                                    >
                                                        {markMatch(
                                                            license.license_key,
                                                            qn
                                                        ) as any}
                                                    </div>
                                                </div>

                                                {/* وضعیت و تاریخ‌ها */}
                                                <div className="space-y-2">
                                                    <div className="text-[11px] text-gray-500 font-semibold">
                                                        وضعیت و مهلت
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <Badge
                                                            variant={activeVariant(
                                                                license.status
                                                            )}
                                                            className="text-xs px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700"
                                                        >
                                                            {humanActive(license.status)}
                                                        </Badge>
                                                        <DaysCounter
                                                            expiresAt={license.expires_at}
                                                            status={license.status}
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2 mt-2 text-[12px] text-gray-700">
                                                        <div className="flex items-center gap-2 bg-white/80 rounded-xl px-3 py-2 border border-sky-100 shadow-[0_0_0_1px_rgba(59,130,246,0.03)]">
                                                            <Calendar className="h-4 w-4 text-sky-600" />
                                                            <div>
                                                                <div className="text-[10px] text-sky-700 font-medium">
                                                                    تاریخ صدور
                                                                </div>
                                                                <div className="font-semibold text-sky-900">
                                                                    {faDate(license.issued_at)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 bg-white/80 rounded-xl px-3 py-2 border border-orange-100 shadow-[0_0_0_1px_rgba(249,115,22,0.04)]">
                                                            <Clock className="h-4 w-4 text-orange-600" />
                                                            <div>
                                                                <div className="text-[10px] text-orange-700 font-medium">
                                                                    تاریخ انقضا
                                                                </div>
                                                                <div className="font-semibold text-orange-900">
                                                                    {faDate(license.expires_at)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* دستگاه‌ها */}
                                                <div className="space-y-2">
                                                    <div className="text-[11px] text-gray-500 font-semibold">
                                                        دستگاه‌های متصل
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100">
                                                            <Cpu className="h-4 w-4 text-purple-600" />
                                                            <span className="text-xs font-semibold text-purple-900">
                                                                {deviceCount > 0
                                                                    ? `${fa.format(
                                                                        deviceCount
                                                                    )} دستگاه`
                                                                    : 'بدون دستگاه متصل'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ردیف پایین: کپی کلید (موبایل) */}
                                            <div className="mt-4 flex flex-col gap-2 md:hidden">
                                                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 border border-gray-200">
                                                    <span className="text-xs text-gray-600 font-medium">
                                                        کپی کلید
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-3 border-emerald-300 hover:bg-emerald-50 text-emerald-700"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(
                                                                license.license_key
                                                            );
                                                            const el =
                                                                document.createElement(
                                                                    'div'
                                                                );
                                                            el.textContent =
                                                                'کلید کپی شد';
                                                            el.className =
                                                                'fixed bottom-6 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs px-3 py-1 rounded-full shadow';
                                                            document.body.appendChild(
                                                                el
                                                            );
                                                            setTimeout(
                                                                () => el.remove(),
                                                                1200
                                                            );
                                                        }}
                                                    >
                                                        <Clipboard className="h-3.5 w-3.5 ml-1" />
                                                        کپی
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ستون کناری کارت پرواز */}
                                        <div className="w-full md:w-52 p-4 flex flex-col justify-between bg-gradient-to-b from-emerald-50/80 to-white">
                                            <div className="flex flex-col gap-2">
                                                {deviceCount > 0 && (
                                                    <Button
                                                        variant="outline"
                                                        className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2"
                                                        onClick={() =>
                                                            toggleExpanded(
                                                                license.license_key
                                                            )
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronUp className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronDown className="h-4 w-4" />
                                                        )}
                                                        مدیریت دستگاه‌ها
                                                    </Button>
                                                )}

                                                {/* ✅ دکمه درخواست جمع‌آوری فقط تا ۳ روز اول و اگر هنوز درخواست ثبت نشده */}
                                                {false && (
                                                // {showCancelButton && (
                                                    <Button
                                                        variant="outline"
                                                        className="w-full border-red-300 text-red-700 hover:bg-red-50 flex items-center justify-center gap-2"
                                                        onClick={() => {
                                                            setSelectedLicense(license);
                                                            setModalOpen(true);
                                                        }}
                                                    >
                                                        <XCircle className="h-4 w-4" />
                                                        لغو
                                                    </Button>
                                                )}

                                                {license.status.toLowerCase() ===
                                                    'expired' && (
                                                        <Button
                                                            variant="outline"
                                                            className="w-full border-emerald-400 text-emerald-800 hover:bg-emerald-100 flex items-center justify-center gap-2"
                                                            onClick={() =>
                                                                alert(
                                                                    'تمدید لایسنس (Placeholder)'
                                                                )
                                                            }
                                                        >
                                                            <RefreshCw className="h-4 w-4" />
                                                            تمدید لایسنس
                                                        </Button>
                                                    )}

                                                {hasCancelRequest(license.license_key) && (
                                                    <div className="text-[11px] text-center text-orange-600 bg-orange-50 border border-orange-100 rounded-lg px-2 py-1 mt-1">
                                                        درخواست جمع‌آوری این لایسنس ثبت شده و در
                                                        حال بررسی است.
                                                    </div>
                                                )}

                                                <div className="mt-1 text-[11px] text-gray-500 text-center">
                                                    آخرین وضعیت:{' '}
                                                    <span className="font-semibold text-emerald-700">
                                                        {humanActive(license.status)}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* بخش بارکد حذف شد تا لایسنس‌کی دوبار نمایش داده نشود */}
                                        </div>
                                    </div>

                                    {/* آکاردئون دستگاه‌ها / بدون دستگاه */}
                                    <div className="border-t border-dashed border-emerald-100 bg-gray-50/70">
                                        {deviceCount > 0 ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-emerald-800 hover:bg-emerald-50 transition-colors"
                                                    onClick={() =>
                                                        toggleExpanded(
                                                            license.license_key
                                                        )
                                                    }
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <Cpu className="h-4 w-4" />
                                                        مشاهده دستگاه‌های متصل (
                                                        {fa.format(deviceCount)})
                                                    </span>
                                                    {isExpanded ? (
                                                        <ChevronUp className="h-5 w-5" />
                                                    ) : (
                                                        <ChevronDown className="h-5 w-5" />
                                                    )}
                                                </button>

                                                {isExpanded && (
                                                    <div className="px-4 pb-4 pt-2 space-y-2 animate-in slide-in-from-top-2 duration-200">
                                                        {license.devices!.map(
                                                            (
                                                                device: ClientDevice,
                                                                idx: number
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        device.id ?? idx
                                                                    }
                                                                    className="bg-gradient-to-r from-emerald-50 to-emerald-50/60 rounded-xl p-4 border border-emerald-100 hover:shadow-md transition-all"
                                                                >
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                        <div>
                                                                            <div className="text-[11px] text-emerald-700 font-semibold mb-1">
                                                                                نام / عنوان دستگاه
                                                                            </div>
                                                                            <div className="text-sm text-gray-900 font-semibold">
                                                                                {device.title ||
                                                                                    device.agent_info ||
                                                                                    device.hardware_code ||
                                                                                    'دستگاه بدون نام'}
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <div className="text-[11px] text-emerald-700 font-semibold mb-1">
                                                                                Agent Info
                                                                            </div>
                                                                            <div className="text-xs text-gray-900">
                                                                                {device.agent_info ||
                                                                                    '—'}
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <div className="text-[11px] text-emerald-700 font-semibold mb-1">
                                                                                کد سخت‌افزار
                                                                            </div>
                                                                            <code
                                                                                className="text-sm text-gray-900 font-mono break-all"
                                                                                dir="ltr"
                                                                            >
                                                                                {device.hardware_code ||
                                                                                    '—'}
                                                                            </code>
                                                                        </div>
                                                                        <div>
                                                                            <div className="text-[11px] text-emerald-700 font-semibold mb-1">
                                                                                MAC Address
                                                                            </div>
                                                                            <code
                                                                                className="text-sm text-gray-900 font-mono"
                                                                                dir="ltr"
                                                                            >
                                                                                {device.mac ||
                                                                                    '—'}
                                                                            </code>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mt-3 pt-3 border-t border-emerald-100 text-[11px] text-emerald-700 flex flex-wrap gap-4">
                                                                        <span>
                                                                            وضعیت:{' '}
                                                                            <span className="font-semibold">
                                                                                {humanActive(
                                                                                    device.status
                                                                                )}
                                                                            </span>
                                                                        </span>
                                                                        {device.activated_at && (
                                                                            <span>
                                                                                اولین اتصال:{' '}
                                                                                {faDate(
                                                                                    device.activated_at
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                        {device.last_seen_at && (
                                                                            <span>
                                                                                آخرین فعالیت:{' '}
                                                                                {faDate(
                                                                                    device.last_seen_at
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-[12px] text-gray-600 flex items-center gap-2">
                                                <Cpu className="h-4 w-4 text-gray-400" />
                                                <span>بدون دستگاه متصل</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* کنترل صفحه‌بندی */}
                        <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                            <CardContent className="py-4">
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-gray-600 font-medium">
                                            نمایش:
                                        </span>
                                        <select
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(
                                                    parseInt(e.target.value, 10)
                                                );
                                                setPage(1);
                                            }}
                                            className="h-10 rounded-xl border border-emerald-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        >
                                            <option value={5}>۵ مورد</option>
                                            <option value={10}>۱۰ مورد</option>
                                            <option value={20}>۲۰ مورد</option>
                                            <option value={50}>۵۰ مورد</option>
                                        </select>
                                    </div>

                                    <div className="text-sm text-gray-700 text-center font-medium bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                                        صفحه{' '}
                                        <span className="text-emerald-700 font-bold">
                                            {fa.format(page)}
                                        </span>{' '}
                                        از{' '}
                                        <span className="text-emerald-700 font-bold">
                                            {fa.format(totalPages)}
                                        </span>
                                        {' — '}
                                        <span className="text-emerald-700 font-bold">
                                            {fa.format(totalItems)}
                                        </span>{' '}
                                        لایسنس
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                setPage((p) => Math.max(1, p - 1))
                                            }
                                            disabled={page <= 1}
                                            className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl"
                                        >
                                            <ChevronRight className="ml-1 h-4 w-4" />
                                            قبلی
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                setPage((p) =>
                                                    Math.min(totalPages, p + 1)
                                                )
                                            }
                                            disabled={page >= totalPages}
                                            className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl"
                                        >
                                            بعدی
                                            <ChevronLeft className="mr-1 h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* ✅ جدول / کارت درخواست‌های جمع‌آوری از بک‌اند */}
                    {cancelRequests.length > 0 && (
                        <Card className="border-amber-200/70 shadow-lg bg-white/90 backdrop-blur-sm">
                            <CardHeader className="border-b border-amber-100 bg-amber-50/60">
                                <div className="flex items-center gap-2">
                                    <XCircle className="h-5 w-5 text-amber-700" />
                                    <div>
                                        <CardTitle className="text-amber-900 text-base">
                                            درخواست‌های جمع‌آوری لایسنس
                                        </CardTitle>
                                        <CardDescription className="text-amber-700">
                                            این بخش برای پیگیری لایسنس‌هایی است که شما درخواست جمع‌آوری آن‌ها را ثبت کرده‌اید.
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-4">
                                {/* دسکتاپ: جدول */}
                                <div className="hidden md:block">
                                    <div className="overflow-x-auto rounded-xl border border-amber-100">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-amber-50">
                                            <tr className="text-right text-amber-900">
                                                <th className="px-3 py-2 border-b border-amber-100">
                                                    #
                                                </th>
                                                <th className="px-3 py-2 border-b border-amber-100">
                                                    کلید لایسنس
                                                </th>
                                                <th className="px-3 py-2 border-b border-amber-100">
                                                    محصول
                                                </th>
                                                <th className="px-3 py-2 border-b border-amber-100">
                                                    تاریخ درخواست
                                                </th>
                                                <th className="px-3 py-2 border-b border-amber-100">
                                                    وضعیت
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {cancelRequests.map((r, idx) => (
                                                <tr
                                                    key={r.id}
                                                    className="odd:bg-white even:bg-amber-50/40"
                                                >
                                                    <td className="px-3 py-2 border-b border-amber-50 text-amber-900">
                                                        {fa.format(idx + 1)}
                                                    </td>
                                                    <td
                                                        className="px-3 py-2 border-b border-amber-50 font-mono text-xs"
                                                        dir="ltr"
                                                    >
                                                        {r.license_key}
                                                    </td>
                                                    <td className="px-3 py-2 border-b border-amber-50 text-gray-800">
                                                        {r.product_title || '—'}
                                                    </td>
                                                    <td className="px-3 py-2 border-b border-amber-50 text-gray-700">
                                                        {faDate(r.created_at)}
                                                    </td>
                                                    <td className="px-3 py-2 border-b border-amber-50">
                                                        <span
                                                            className={
                                                                'inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ' +
                                                                (r.status === 'pending'
                                                                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                                                    : r.status === 'processed'
                                                                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                                        : 'bg-red-100 text-red-800 border border-red-200')
                                                            }
                                                        >
                                                            {r.status === 'pending'
                                                                ? 'در انتظار بررسی'
                                                                : r.status === 'processed'
                                                                    ? 'پردازش‌شده'
                                                                    : 'رد شده'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* موبایل: کارت‌ها */}
                                <div className="md:hidden space-y-3">
                                    {cancelRequests.map((r, idx) => (
                                        <div
                                            key={r.id}
                                            className="rounded-2xl border border-amber-200 bg-white shadow-sm p-3 flex flex-col gap-2"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] text-gray-500">
                                                        درخواست #{fa.format(idx + 1)}
                                                    </span>
                                                    <span
                                                        className="font-mono text-xs text-gray-800"
                                                        dir="ltr"
                                                    >
                                                        {r.license_key}
                                                    </span>
                                                </div>
                                                <span
                                                    className={
                                                        'inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium ' +
                                                        (r.status === 'pending'
                                                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                                            : r.status === 'processed'
                                                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                                : 'bg-red-100 text-red-800 border border-red-200')
                                                    }
                                                >
                                                    {r.status === 'pending'
                                                        ? 'در انتظار بررسی'
                                                        : r.status === 'processed'
                                                            ? 'پردازش‌شده'
                                                            : 'رد شده'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-[11px] text-gray-600">
                                                <span>محصول:</span>
                                                <span className="font-medium">
                                                    {r.product_title || '—'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-[11px] text-gray-600">
                                                <span>تاریخ درخواست:</span>
                                                <span>{faDate(r.created_at)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* مودال ثبت درخواست جمع‌آوری */}
                    <CollectRequestModal
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                        onSubmit={submitCollectRequest}
                        loading={collectLoadingId !== null}
                        licenseTitle={selectedLicense?.product_title || ''}
                        licenseKey={selectedLicense?.license_key || ''}
                    />
                </>
            )}
        </div>
    );
};

export default Licenses;
